import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import z from 'zod'
import { AppLoader } from '~/components/loader'
import { useTitle } from '~/hooks'
import { useTRPC } from '~/services/trpc'
import { PublicTemplate } from '~/templates/public'

import { Button, Icon } from '@hap/components'

export const searchParamsSchema = z.object({
  token: z.string().optional(),
})

export const Route = createFileRoute(
  '/_authenticated/$alias/integrations/$integrationId/create-contact',
)({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
})

function RouteComponent() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const search = Route.useSearch()
  const trpc = useTRPC()

  const mutation = useMutation(trpc.myCase.createContact.mutationOptions())

  useTitle('MyCase - Create Contact')

  const handleCreateContact = async () => {
    if (!search.token) {
      setError('Missing token. Please use the link from your email.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      await mutation.mutateAsync({
        token: search.token,
      })
      setSuccess(true)
    } catch (e) {
      console.error(e)
      setError(
        e instanceof Error
          ? e.message
          : 'Failed to create contact in MyCase. Please reach out to support at help@happitu.com',
      )
    } finally {
      setLoading(false)
    }
  }

  // Show error if no token
  if (!search.token) {
    return (
      <PublicTemplate width={400}>
        <div className="bg-accent text-center">
          <div className="flex flex-col content-center justify-center space-y-6 p-8 pb-16">
            <Icon
              name="warning-diamond"
              size="60"
              className="fill-earth-yellow mx-auto h-[100px]"
              weight="fill"
            />
            <p>Invalid link. Please use the link from your email notification.</p>
          </div>
        </div>
      </PublicTemplate>
    )
  }

  if (loading) return <AppLoader />

  if (error) {
    return (
      <PublicTemplate width={400}>
        <div className="bg-accent text-center">
          <div className="flex flex-col content-center justify-center space-y-6 p-8 pb-16">
            <Icon
              name="warning-diamond"
              size="60"
              className="fill-earth-yellow mx-auto h-[100px]"
              weight="fill"
            />
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p>{error}</p>
            <Button onClick={() => setError(null)}>Try Again</Button>
          </div>
        </div>
      </PublicTemplate>
    )
  }

  if (success) {
    return (
      <PublicTemplate>
        <div className="bg-accent space-y-10 px-8 py-10 text-center">
          <div className="flex justify-center gap-2">
            <div className="flex aspect-square h-[100px] shrink-0 items-center justify-center rounded-md bg-white p-4">
              <img
                src="https://www.mycase.com/favicon.png"
                className="w-fit"
                alt="MyCase"
              />
            </div>
          </div>

          <Icon
            name="check-circle"
            size="60"
            className="fill-earth-green mx-auto h-[100px]"
            weight="fill"
          />

          <h1 className="text-center text-2xl font-semibold">
            Contact Created Successfully!
          </h1>
          <p>
            The contact has been created in MyCase and the call record has been added. You
            may close this window.
          </p>
        </div>
      </PublicTemplate>
    )
  }

  return (
    <PublicTemplate>
      <div className="bg-accent space-y-10 px-8 py-10 text-center">
        <div className="flex justify-center gap-2">
          <div className="flex aspect-square h-[100px] shrink-0 items-center justify-center rounded-md bg-white p-4">
            <img
              src="https://www.mycase.com/favicon.png"
              className="w-fit"
              alt="MyCase"
            />
          </div>
        </div>

        <h1 className="text-center text-2xl font-semibold">Create Contact in MyCase</h1>
        <p>
          A contact could not be found in MyCase for this call. Click the button below to
          automatically create the contact and add the call record.
        </p>

        <Button onClick={handleCreateContact} size="lg">
          Create Contact & Add Call Record
        </Button>
      </div>
    </PublicTemplate>
  )
}
